import { z } from 'zod';

import { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererKeyToPayload,
	type ActionValidationResult,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

const payloadSchema = z.object({
	projectId: z.string(),
	phase: z.enum(['paused', 'pending', 'in_progress', 'done', 'cancelled']).optional(),
	status: z.enum(['on_track', 'at_risk', 'off_track']).optional(),
	summary: z.string().optional(),
	targetDate: z
		.object({
			date: z.string().optional(),
			confidence: z
				.string()
				.transform((val) => val.toUpperCase())
				.pipe(z.enum(['DAY', 'MONTH', 'QUARTER']))
				.optional(),
		})
		.optional(),
	updateNote: z
		.object({
			summary: z.string().optional(),
			description: z.string(),
		})
		.optional(),
	highlightLearning: z
		.object({
			summary: z.string(),
			description: z.string(),
			type: z
				.string()
				.transform((val) => val.toUpperCase())
				.pipe(z.literal('LEARNING')),
		})
		.optional(),
	highlightRisk: z
		.object({
			summary: z.string(),
			description: z.string(),
			type: z
				.string()
				.transform((val) => val.toUpperCase())
				.pipe(z.literal('RISK')),
		})
		.optional(),
	highlightDecision: z
		.object({
			summary: z.string(),
			description: z.string(),
			type: z
				.string()
				.transform((val) => val.toUpperCase())
				.pipe(z.literal('DECISION')),
		})
		.optional(),
});

const actionSchema = z.object({
	key: z.literal(ActionKeys.CreateProjectProgressUpdate),
	data: payloadSchema,
});

export function validateCreateProjectProgressUpdateAction(
	action: MessageAction,
): ActionValidationResult<
	ActionRendererKeyToPayload[typeof ActionKeys.CreateProjectProgressUpdate]
> {
	const parsed = actionSchema.safeParse(action);
	if (parsed.success) {
		return {
			isValid: true,
			action: action as ActionWith<
				ActionRendererKeyToPayload[typeof ActionKeys.CreateProjectProgressUpdate]
			>,
		};
	}

	return {
		isValid: false,
		error: new Error(`Invalid ${action.key || ''} action payload: ${parsed.error.message}`),
	};
}
